import {
	applyDocumentLocale,
	createTranslate,
	ENGLISH,
	type Locale,
	loadMessages,
	type Messages,
	rememberLocale,
	storedLocale,
	type Translate,
} from "@khazzin/i18n";
import { UiTextProvider } from "@khazzin/ui/primitives/uiText";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

/**
 * The Driver App's language: what this phone last chose, switched from the
 * Profile tab. No server round trip — a driver switching to Arabic in a
 * basement must get Arabic now.
 */

interface I18n {
	locale: Locale;
	t: Translate;
	setLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18n | null>(null);

export function useI18n(): I18n {
	const value = useContext(I18nContext);
	if (value === null) {
		throw new Error("useI18n must be used inside <I18nProvider>");
	}
	return value;
}

export function I18nProvider({ children }: { children: ReactNode }) {
	const [locale, setLocaleState] = useState<Locale>(storedLocale);
	const [messages, setMessages] = useState<Messages>(ENGLISH);
	useEffect(() => {
		let live = true;
		void loadMessages(locale).then((loaded) => {
			if (live) {
				setMessages(loaded);
			}
		});
		return () => {
			live = false;
		};
	}, [locale]);

	const value = useMemo<I18n>(
		() => ({
			locale,
			t: createTranslate(locale, messages),
			setLocale(next) {
				rememberLocale(next);
				applyDocumentLocale(next);
				setLocaleState(next);
			},
		}),
		[locale, messages],
	);
	// ADJ-M03-1: the design system's own words ("More", "Search") follow the locale.
	return (
		<I18nContext.Provider value={value}>
			<UiTextProvider t={value.t}>{children}</UiTextProvider>
		</I18nContext.Provider>
	);
}
